import { action, makeObservable } from 'mobx';

import {
	AdditionalBookingOptionEntity,
	CargoEntity,
	FerryTripEntity,
	FreightDetailEntity,
	RefundEntity,
	TowOnEntity,
} from 'Models/Entities';
import { AlterationContext } from 'Models/Entities/AlterationEntity';
import { isNotNullOrUndefined, isNullOrUndefined, stringNotEmpty } from 'Util/TypeGuards';
import { SelectedTicketsDto } from 'Views/Components/_HumanWritten/BookingWizard/EventsBookingWizard/EventsBookingWizardData';
import { PassengerDetail } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/PassengerDetail';
import { EffectiveCargoCharacteristicDto } from 'Modules/Cargo';

export interface BookingSummaryAddon {
	quantity: number;
	option: AdditionalBookingOptionEntity;
}
export interface BookingSummaries {
	recentSummary: BookingSummary;
	previousSummary?: BookingSummary;
}

export interface IBookingSummary {
	// Shared properties (event and ferry booking)
	id: string;
	userId: string;
	userFirstName: string;
	userLastName: string;
	ferryTrip: FerryTripEntity;
	/**
	 * Whether this leg's vessel has seats to hand out at all.
	 *
	 * <p>Undefined where the payload did not work it out — most of them — which every consumer must
	 * read as "carry on as before" rather than as false. Only an explicit false says the vessel sells
	 * unreserved seating, and so that no seat is coming for anybody on it at check-in.</p>
	 */
	tripHasSeats?: boolean;
	totalCost: number;
	paidBookingPrice: number;
	maxRefund: number;
	nonRefundableAmount: number;
	discountAmount: number;
	invoiced: boolean;
	discounted: boolean;

	// Ferry booking only properties
	tripDateTime: Date;
	startStopId: string;
	endStopId: string;
	adultPassengerTickets: PassengerDetail[];
	childPassengerTickets: PassengerDetail[];
	infantPassengerTickets: PassengerDetail[];
	passengerDTickets?: PassengerDetail[];
	passengerETickets?: PassengerDetail[];
	passengerFTickets?: PassengerDetail[];
	passengerGTickets?: PassengerDetail[];
	passengerHTickets?: PassengerDetail[];
	driverPhone: string;
	driverFirstName: string;
	driverLastName: string;
	cargoInfo?: CargoEntity | null;
	towOnInfo?: TowOnEntity | null;
	additionalBookingOptions: BookingSummaryAddon[];
	note?: string;

	// Event booking only properties
	eventId?: string;
	selectedTickets?: SelectedTicketsDto[];
	primaryFirstName?: string;
	primaryLastName?: string;
	primaryPhone?: string;
	primaryEmail?: string;

	// Freight booking only properties
	freightDetail?: FreightDetailEntity;
	freightCalculation?: string;

	alterationContext?: AlterationContext;

	// Refunds
	refunds?: RefundEntity[];

	effectiveCharacteristics?: EffectiveCargoCharacteristicDto[];

	trailerEffectiveCharacteristics?: EffectiveCargoCharacteristicDto[];

	// Parking reservations
	parkingReservations?: ParkingReservationSummary[];
}

export interface ParkingReservationSummary {
	id: string;
	humanReadableId: string;
	status: string;
	totalPrice: number;
	locationName: string;
	locationAddress: string;
	note: string;
	vehicles: ParkingVehicleSummary[];
}

export interface ParkingVehicleSummary {
	rego: string;
	startDate: string;
	endDate: string;
	dailyRate: number;
	subTotal: number;
}

export class BookingSummary implements IBookingSummary {
	constructor(attributes?: Partial<IBookingSummary>) {
		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	private assignAttributes(attributes?: Partial<IBookingSummary>) {
		if (isNullOrUndefined(attributes)) {
			return;
		}

		// Shared properties (event and ferry booking)
		if (attributes.id !== undefined) {
			this.id = attributes.id;
		}
		if (attributes.userId !== undefined) {
			this.userId = attributes.userId;
		}
		if (attributes.userFirstName !== undefined) {
			this.userFirstName = attributes.userFirstName;
		}
		if (attributes.userLastName !== undefined) {
			this.userLastName = attributes.userLastName;
		}
		if (isNotNullOrUndefined(attributes.ferryTrip)) {
			this.ferryTrip = new FerryTripEntity(attributes.ferryTrip);
		}
		if (attributes.tripHasSeats !== undefined) {
			this.tripHasSeats = attributes.tripHasSeats;
		}
		if (attributes.totalCost !== undefined) {
			this.totalCost = attributes.totalCost;
		}
		if (attributes.paidBookingPrice !== undefined) {
			this.paidBookingPrice = attributes.paidBookingPrice;
		}
		if (attributes.maxRefund !== undefined) {
			this.maxRefund = attributes.maxRefund;
		}
		if (attributes.nonRefundableAmount !== undefined) {
			this.nonRefundableAmount = attributes.nonRefundableAmount;
		}
		if (attributes.discountAmount !== undefined) {
			this.discountAmount = attributes.discountAmount;
		}
		if (attributes.invoiced !== undefined) {
			this.invoiced = attributes.invoiced;
		}
		if (attributes.discounted !== undefined) {
			this.discounted = attributes.discounted;
		}

		// Ferry booking only properties
		if (attributes.adultPassengerTickets !== undefined) {
			this.adultPassengerTickets = attributes.adultPassengerTickets;
		}
		if (attributes.childPassengerTickets !== undefined) {
			this.childPassengerTickets = attributes.childPassengerTickets;
		}
		if (attributes.infantPassengerTickets !== undefined) {
			this.infantPassengerTickets = attributes.infantPassengerTickets;
		}
		if (isNotNullOrUndefined(attributes.passengerDTickets)) {
			this.passengerDTickets = attributes.passengerDTickets;
		}
		if (isNotNullOrUndefined(attributes.passengerETickets)) {
			this.passengerETickets = attributes.passengerETickets;
		}
		if (isNotNullOrUndefined(attributes.passengerFTickets)) {
			this.passengerFTickets = attributes.passengerFTickets;
		}
		if (isNotNullOrUndefined(attributes.passengerGTickets)) {
			this.passengerGTickets = attributes.passengerGTickets;
		}
		if (isNotNullOrUndefined(attributes.passengerHTickets)) {
			this.passengerHTickets = attributes.passengerHTickets;
		}
		if (attributes.driverPhone !== undefined) {
			this.driverPhone = attributes.driverPhone;
		}
		if (attributes.driverFirstName !== undefined) {
			this.driverFirstName = attributes.driverFirstName;
		}
		if (attributes.driverLastName !== undefined) {
			this.driverLastName = attributes.driverLastName;
		}
		if (attributes.cargoInfo !== undefined) {
			if (attributes.cargoInfo === null) {
				this.cargoInfo = null;
			} else if (attributes.cargoInfo instanceof CargoEntity) {
				this.cargoInfo = attributes.cargoInfo;
			} else {
				this.cargoInfo = new CargoEntity(attributes.cargoInfo);
			}
		}
		if (attributes.towOnInfo !== undefined) {
			if (attributes.towOnInfo === null) {
				this.towOnInfo = null;
			} else if (attributes.towOnInfo instanceof TowOnEntity) {
				this.towOnInfo = attributes.towOnInfo;
			} else {
				this.towOnInfo = new TowOnEntity(attributes.towOnInfo);
			}
		}
		if (isNotNullOrUndefined(attributes.additionalBookingOptions)) {
			if (Array.isArray(attributes.additionalBookingOptions)) {
				this.additionalBookingOptions = attributes.additionalBookingOptions.map(option => {
					return {
						quantity: option.quantity,
						option:
							option.option instanceof AdditionalBookingOptionEntity
								? option.option
								: new AdditionalBookingOptionEntity(option.option),
					};
				});
			} else {
				this.additionalBookingOptions = attributes.additionalBookingOptions;
			}
		}
		if (attributes.note !== undefined) {
			this.note = attributes.note;
		}

		if (isNotNullOrUndefined(attributes.refunds)) {
			this.refunds = attributes.refunds.map(x => new RefundEntity(x));
		}

		// Feature: Multi-stop - assign summary attributes
		if (attributes.tripDateTime !== undefined) {
			this.tripDateTime = attributes.tripDateTime;
		}
		if (attributes.startStopId !== undefined) {
			this.startStopId = attributes.startStopId;
		}
		if (attributes.endStopId !== undefined) {
			this.endStopId = attributes.endStopId;
		}

		// Event booking only properties
		if (attributes.eventId !== undefined) {
			this.eventId = attributes.eventId;
		}
		if (isNotNullOrUndefined(attributes.selectedTickets)) {
			this.selectedTickets = attributes.selectedTickets;
		}
		if (attributes.primaryFirstName !== undefined) {
			this.primaryFirstName = attributes.primaryFirstName;
		}
		if (attributes.primaryLastName !== undefined) {
			this.primaryLastName = attributes.primaryLastName;
		}
		if (attributes.primaryPhone !== undefined) {
			this.primaryPhone = attributes.primaryPhone;
		}
		if (attributes.primaryEmail !== undefined) {
			this.primaryEmail = attributes.primaryEmail;
		}

		// Freight booking only properties
		if (attributes.freightDetail !== undefined) {
			if (attributes.freightDetail === null) {
				this.freightDetail = undefined;
			} else if (attributes.freightDetail instanceof FreightDetailEntity) {
				this.freightDetail = attributes.freightDetail;
			} else {
				this.freightDetail = new FreightDetailEntity(attributes.freightDetail);
			}
		}
		if (stringNotEmpty(attributes.freightCalculation)) {
			this.freightCalculation = attributes.freightCalculation;
		}

		if (attributes.alterationContext) {
			this.alterationContext = attributes.alterationContext;
		}

		if (isNotNullOrUndefined(attributes.effectiveCharacteristics)) {
			this.effectiveCharacteristics = attributes.effectiveCharacteristics;
		}

		if (isNotNullOrUndefined(attributes.trailerEffectiveCharacteristics)) {
			this.trailerEffectiveCharacteristics = attributes.trailerEffectiveCharacteristics;
		}

		if (isNotNullOrUndefined(attributes.parkingReservations)) {
			this.parkingReservations = attributes.parkingReservations;
		}
	}

	// Shared properties (event and ferry booking)
	id: string;
	userId: string;
	userFirstName: string;
	userLastName: string;
	ferryTrip: FerryTripEntity;

	/**
	 * Whether this leg's vessel has seats to hand out at all.
	 *
	 * <p>Undefined where the payload did not work it out — most of them — which every consumer must
	 * read as "carry on as before" rather than as false. Only an explicit false says the vessel sells
	 * unreserved seating, and so that no seat is coming for anybody on it at check-in.</p>
	 */
	tripHasSeats?: boolean;
	totalCost: number;
	paidBookingPrice: number;
	maxRefund: number;
	nonRefundableAmount: number;
	discountAmount: number;
	invoiced: boolean;
	discounted: boolean;

	// Ferry booking only properties
	adultPassengerTickets: PassengerDetail[];
	childPassengerTickets: PassengerDetail[];
	infantPassengerTickets: PassengerDetail[];
	passengerDTickets?: PassengerDetail[];
	passengerETickets?: PassengerDetail[];
	passengerFTickets?: PassengerDetail[];
	passengerGTickets?: PassengerDetail[];
	passengerHTickets?: PassengerDetail[];
	driverPhone: string;
	driverFirstName: string;
	driverLastName: string;
	cargoInfo?: CargoEntity | null;
	towOnInfo?: TowOnEntity | null;
	additionalBookingOptions: BookingSummaryAddon[];
	note?: string;
	tripDateTime: Date;
	startStopId: string;
	endStopId: string;

	// Event booking only properties
	eventId?: string;
	selectedTickets?: SelectedTicketsDto[];
	primaryFirstName?: string;
	primaryLastName?: string;
	primaryPhone?: string;
	primaryEmail?: string;

	// Freight booking only properties
	freightDetail?: FreightDetailEntity;
	freightCalculation?: string;

	alterationContext?: AlterationContext;

	// Refunds
	refunds?: RefundEntity[];

	effectiveCharacteristics?: EffectiveCargoCharacteristicDto[];

	trailerEffectiveCharacteristics?: EffectiveCargoCharacteristicDto[];

	// Parking reservations
	parkingReservations?: ParkingReservationSummary[];
}
